import { Play } from '@tunasong/icons'
import { METER_POOL } from '@tunasong/music-lib'
import type { MeterQuestion, MeterVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './meter-feedback.module.css'

export interface MeterFeedbackProps {
  question: MeterQuestion
  verdict: MeterVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Rhythm Trainer Meter ID post-answer reveal. Renders:
 *  - Verdict header ("Correct." / "Not quite.") in success/error colour.
 *  - Correct-answer sentence naming the meter + (on incorrect) the
 *    learner's pick. Format: "That was 6/8. You picked 3/4."
 *  - Level-resolved lexicon body for the meter (grouping + feel landmark).
 *  - "Hear it again" + manual "Next" actions (mirrors Ear Training).
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §6.3
 */
export const MeterFeedback: FC<MeterFeedbackProps> = props => {
  const { verdict, onHearAgain, onNext } = props
  const correctEntry = verdict.entry
  const pickedEntry = METER_POOL.find(e => e.kind === verdict.picked) ?? correctEntry
  const lexicon = useTheoryCopy(correctEntry.lexiconId)
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        {verdict.correct ? (
          <>That was {correctEntry.displayName}.</>
        ) : (
          <>
            That was {correctEntry.displayName}. You picked {pickedEntry.displayName}.
          </>
        )}
      </p>
      {lexBody ? <p className={styles.body}>{lexBody}</p> : null}
      <div className={styles.actions}>
        <Button variant="text" size="small" startIcon={<Play />} onClick={onHearAgain}>
          Hear it again
        </Button>
        <Button variant="contained" color="primary" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
